const ETH_USD_TTL_MS = 60_000
const COINGECKO_ETH_USD_URL = 'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd'

let cached: { price: number, at: number } | null = null
let inflight: Promise<number | null> | null = null

function readCachedPrice(): number | null {
  if (!cached)
    return null
  // Never serve an expired quote as a fallback after a failed refresh —
  // showing "—" until the next successful fetch is better than silently
  // pricing market cap off a stale CoinGecko quote.
  if (Date.now() - cached.at > ETH_USD_TTL_MS)
    return null
  return cached.price
}

export function getCachedEthUsdPrice(): number | null {
  if (!cached || !(cached.price > 0))
    return null
  // Same expiry rule for the synchronous initial read used by
  // useEthUsdPrice: an expired cache must not be shown while a fresh
  // fetch is in flight.
  if (Date.now() - cached.at > ETH_USD_TTL_MS)
    return null
  return cached.price
}

export function formatUsdFromEthWei(wei: bigint, ethUsd: number | null): string {
  if (typeof wei !== 'bigint' || !ethUsd || ethUsd <= 0)
    return '—'
  const eth = Number(wei) / 1e18
  const usd = eth * ethUsd
  if (!Number.isFinite(usd) || usd <= 0)
    return '$0'
  if (usd >= 1_000_000_000)
    return `$${(usd / 1_000_000_000).toFixed(2)}B`
  if (usd >= 1_000_000)
    return `$${(usd / 1_000_000).toFixed(2)}M`
  if (usd >= 1_000)
    return `$${(usd / 1_000).toFixed(2)}K`
  if (usd >= 1)
    return `$${usd.toFixed(2)}`
  return `$${usd.toFixed(4)}`
}

export async function fetchEthUsdPrice(): Promise<number | null> {
  const fresh = cached && Date.now() - cached.at < ETH_USD_TTL_MS ? cached.price : null
  if (fresh && fresh > 0)
    return fresh
  if (inflight)
    return inflight

  inflight = (async () => {
    try {
      const response = await fetch(COINGECKO_ETH_USD_URL)
      if (!response.ok)
        return readCachedPrice()
      const data = await response.json() as { ethereum?: { usd?: number } }
      const price = Number(data?.ethereum?.usd)
      if (!Number.isFinite(price) || price <= 0)
        return readCachedPrice()
      cached = { price, at: Date.now() }
      return price
    }
    catch {
      return readCachedPrice()
    }
    finally {
      inflight = null
    }
  })()

  return inflight
}
